<!-- Practice Area Settings (per-office, per-practice-area capacity settings) -->
<mat-card class="details-card practice-area-settings-card">
  <div class="practice-area-settings-section">
    <div class="card-heading">
      <div class="section-header">
        <div class="section-header__titles">
          <h3 class="pas-card-heading">
            <button
              type="button"
              class="pas-collapse-toggle"
              (click)="cardExpanded = !cardExpanded"
              [attr.aria-expanded]="cardExpanded"
            >
              <mat-icon
                class="expansion-indicator"
                [style.transform]="
                  cardExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                "
                >expand_more</mat-icon
              >
              Practice Area Settings
            </button>
          </h3>
        </div>
      </div>
      <!-- Sits outside the collapsible body on purpose, so a collapsed card still says
           what it is — matching the notifications card. The detail (who it applies to)
           lives in the blue note inside. -->
      <p class="card-description">
        Set the capacity period and expected-hours requirement for practice
        areas in this office.
      </p>
    </div>

    @if (cardExpanded) {
      <div class="pas-body" @expandCollapse>
        @if (isLoading) {
          <div class="pas-status">
            <mat-spinner diameter="28"></mat-spinner>
            <span>Loading practice area settings…</span>
          </div>
        } @else if (loadError) {
          <div class="pas-status pas-status--error" role="alert">
            <mat-icon>error_outline</mat-icon>
            <span>{{ loadError }}</span>
            <button type="button" mat-stroked-button (click)="retry()">
              Retry
            </button>
          </div>
        } @else {
          <!-- Same two-column grid as the notifications card, with only the first column
               filled. Keeps this card's content the width of that card's left column
               instead of stretching the table across the full page, and collapses to full
               width at the same breakpoint. -->
          <div class="pas-grid">
            <div class="pas-col">
              <div class="pas-note pas-note--info">
                <mat-icon class="pas-note__icon">info</mat-icon>
                <span>
                  These settings apply to resources whose
                  <strong>primary</strong> office is
                  <strong>{{ officeDescription || "this office" }}</strong
                  >. A resource's capacity period follows their
                  <strong>primary</strong> practice area only — a two-week
                  period set on one of their secondary practice areas does not
                  change it. Custom practice areas are not listed: a resource's
                  primary practice area always comes from the firm directory.
                </span>
              </div>

              <div class="pas-note pas-note--warn">
                <mat-icon class="pas-note__icon">warning</mat-icon>
                <span>
                  Switching a practice area to <strong>2 weeks</strong> during
                  the second week of the current cycle locks its resources out
                  of the current period until the next cycle starts. Switching
                  one back to <strong>1 week</strong> during that week reopens
                  the closed period for every resource in the practice area.
                  Configure during week 1 where possible.
                </span>
              </div>

              <div class="pas-subsection">
                <div class="section-header">
                  <div class="section-header__titles pas-subsection__titles">
                    <h4 class="pas-subtitle">Practice areas</h4>
                    @if (expectedHoursEnabled) {
                      <p class="pas-subdesc">
                        Turning on <strong>Require expected hours</strong> makes
                        them mandatory for resources whose
                        <strong>primary</strong> practice area it is. All others
                        stay optional.
                      </p>
                    }
                  </div>
                  <div class="pa-counts">
                    <span class="pa-count">
                      <strong>{{ twoWeekPracticeAreaCount }}</strong> of
                      {{ practiceAreas.length }} on a 2-week period
                    </span>
                    @if (expectedHoursEnabled) {
                      <span class="pa-count">
                        <strong>{{ requiredPracticeAreaCount }}</strong> of
                        {{ practiceAreas.length }} require expected hours
                      </span>
                    }
                  </div>
                </div>

                <div class="pa-toolbar">
                  <mat-form-field
                    appearance="outline"
                    class="pa-search"
                    subscriptSizing="dynamic"
                  >
                    <input
                      matInput
                      [ngModel]="practiceAreaFilter"
                      (ngModelChange)="onPracticeAreaFilterChange($event)"
                      placeholder="Filter practice areas"
                      aria-label="Filter practice areas"
                    />
                    <mat-icon
                      matSuffix
                      [style.cursor]="
                        practiceAreaFilter ? 'pointer' : 'default'
                      "
                      (click)="clearPracticeAreaFilter()"
                      >{{ practiceAreaFilter ? "close" : "search" }}</mat-icon
                    >
                  </mat-form-field>
                </div>

                <div class="pa-table-wrap">
                  <table
                    mat-table
                    [dataSource]="practiceAreaDataSource"
                    class="pa-table"
                  >
                    <!-- Name + code -->
                    <ng-container matColumnDef="practiceArea">
                      <th mat-header-cell *matHeaderCellDef>Practice area</th>
                      <td mat-cell *matCellDef="let pa">
                        <div class="pa-item__name" [matTooltip]="pa.name">
                          {{ pa.name }}
                        </div>
                        <div class="pa-item__meta">{{ pa.code }}</div>
                      </td>
                    </ng-container>

                    <!-- Capacity period, saved on click -->
                    <ng-container matColumnDef="period">
                      <th
                        mat-header-cell
                        *matHeaderCellDef
                        class="pa-col-period"
                      >
                        Capacity period
                      </th>
                      <td mat-cell *matCellDef="let pa" class="pa-col-period">
                        <mat-button-toggle-group
                          class="pa-period-toggle"
                          [(ngModel)]="pa.weeks"
                          (change)="onPeriodChange(pa)"
                          [disabled]="!canManage"
                          [attr.aria-label]="pa.name + ' capacity period'"
                        >
                          <mat-button-toggle [value]="1"
                            >1 week</mat-button-toggle
                          >
                          <mat-button-toggle [value]="2"
                            >2 weeks</mat-button-toggle
                          >
                        </mat-button-toggle-group>
                      </td>
                    </ng-container>

                    <!-- On/Off label -->
                    <ng-container matColumnDef="state">
                      <!-- The label and the toggle are one logical column to a reader, so head
                       them with a single centred "Require expected hours" spanning both. This
                       also labels the toggle column for assistive tech, which would otherwise
                       announce it as unlabeled. -->
                      <th
                        mat-header-cell
                        *matHeaderCellDef
                        class="pa-col-state"
                        [attr.colspan]="2"
                      >
                        Require<br/> expected hours
                      </th>
                      <td mat-cell *matCellDef="let pa" class="pa-col-state">
                        <span
                          class="pa-item__state"
                          [class.pa-item__state--on]="pa.required"
                          >{{ pa.required ? "On" : "Off" }}</span
                        >
                      </td>
                    </ng-container>

                    <!-- Per-practice-area toggle -->
                    <ng-container matColumnDef="required">
                      <!-- Empty and hidden: the "Require expected hours" header above spans
                       this column, so a second header cell would break the colspan. -->
                      <th
                        mat-header-cell
                        *matHeaderCellDef
                        class="pa-col-toggle"
                      ></th>
                      <td mat-cell *matCellDef="let pa" class="pa-col-toggle">
                        <mat-slide-toggle
                          [(ngModel)]="pa.required"
                          (change)="onExpectedHoursChange(pa)"
                          [disabled]="!canManage"
                          color="primary"
                          class="toggle-success"
                          [hideIcon]="true"
                          [attr.aria-label]="pa.name + ' expected hours required'"
                        ></mat-slide-toggle>
                      </td>
                    </ng-container>

                    <tr
                      mat-header-row
                      *matHeaderRowDef="practiceAreaColumns"
                    ></tr>
                    <tr
                      mat-row
                      *matRowDef="let row; columns: practiceAreaColumns"
                    ></tr>

                    <tr class="mat-row" *matNoDataRow>
                      <td
                        class="mat-cell pa-no-data"
                        [attr.colspan]="practiceAreaColumns.length"
                      >
                        <!-- This row also shows when there are no practice areas at all, so only
                         blame the filter when one is applied. Branch on the data rather than
                         `practiceAreaFilter`, which stays truthy for a whitespace-only entry
                         that filters nothing. -->
                        <div class="pa-empty">
                          @if (practiceAreas.length === 0) {
                            No practice areas are available.
                          } @else {
                            No practice areas match your filter.
                          }
                        </div>
                      </td>
                    </tr>
                  </table>

                  <mat-paginator
                    class="pa-paginator"
                    [pageSize]="practiceAreaPageSize"
                    [pageSizeOptions]="practiceAreaPageSizeOptions"
                    showFirstLastButtons
                    aria-label="Select page of practice areas"
                  ></mat-paginator>
                </div>

                @if (!canManage) {
                  <p class="pas-readonly-note">
                    You have view-only access to these settings.
                  </p>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }
  </div>
</mat-card>
